import { useCallback, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { getUID } from '../analyticsUtils';
import { useAuth } from 'react-oidc-context';

const intervalInSeconds = 5;

// Send events to EventHubs reading window.eventHubDataLayer array every N seconds
export const useEventHubDataLayerConsumer = () => {
  const lastIndexProcessed = useRef<number>(0);
  const auth = useAuth();
  const userId =
    (auth?.user?.profile?.IndividualSK as string)?.length > 0
      ? (auth.user?.profile?.IndividualSK as string)
      : '000000';
  const gaCookie = Cookies.get('_gtm_ga');
  const sessionGuid = gaCookie ? `${gaCookie}.${userId}` : 'NoSession';

  // Replaced async calls with `navigator.sendBeacon()` for reliable data sending during page unload.
  // `sendBeacon` ensures data is sent without being blocked by browser unload restrictions.
  const sendEvents = useCallback((events: string[]) => {
    const dataBlob = new Blob([JSON.stringify(events)], { type: 'application/json' });
    navigator.sendBeacon(
      `${import.meta.env.VITE_CONTENT_DELIVERY_API}/api/client/event-hub/events`,
      dataBlob
    );
  }, []);

  const processEvents = useCallback(async () => {
    // console.debug('Calling useEventHubDataLayerConsumer');
    const currentLastIndex = window.eventHubDataLayer?.length;
    if (currentLastIndex > 0) {
      const eventsToBeProcessed =
        window.eventHubDataLayer.slice(lastIndexProcessed.current, currentLastIndex) ?? [];
      if (eventsToBeProcessed.length > 0) {
        try {
          // console.debug(`Consuming window.eventHubDataLayer. Sending ${eventsToBeProcessed.length} event(s) to eventHub...`);

          const analyticsEventHash = getUID();
          const analyticsSentEventTimestamp = new Date().getTime();
          const jsonEventsToBeProcessed = () =>
            eventsToBeProcessed.map(function (event) {
              const newEvent = {
                ...event,
                analytics_event_hash: analyticsEventHash,
                analytics_sent_event_timestamp: analyticsSentEventTimestamp,
                sessionGuid: sessionGuid,
                dataSource: 'FrontEnd'
              };
              const jsonEvent = JSON.stringify(newEvent);

              return jsonEvent;
            });

          sendEvents(jsonEventsToBeProcessed());
          lastIndexProcessed.current = currentLastIndex;
          return;
        } catch (error) {
          // Handle error if needed on client. Errors and logging were handled on the backend
          //console.warn(error);
        }
      }
    }
  }, [sendEvents, sessionGuid]);

  useEffect(() => {
    const timerInterval = setInterval(async () => {
      processEvents();
    }, intervalInSeconds * 1000);

    return () => {
      clearInterval(timerInterval);
    };
  }, [processEvents]);

  return {
    processEvents
  };
};
